import React, { Fragment, useCallback, useRef, useEffect, useState } from 'react';
import { PlaybookTransformItem, PlaybookEnrichment, PlaybookEventConfig } from '@/types/playbook';
import PropertyPathAutocomplete from '@components/common/PropertyPathAutocomplete';
import ReactDOM from 'react-dom'; // Added ReactDOM
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from '@headlessui/react';
import { Check, ChevronDown } from 'lucide-react';
import clsx from 'clsx';
import { DataType, Parameter, EventSchema } from '@/types/schema';
import { getTypeColorClass } from '@utils/schemaUtils';
import { usePortalRoot } from '@contexts/PortalRootContext'; // Added usePortalRoot
import { useTooltipPosition } from '@hooks/useTooltipPosition'; // Added useTooltipPosition
import {
  TypeIconString,
  TypeIconNumber,
  TypeIconBoolean,
  TypeIconObject,
  TypeIconArray,
  TypeIconNull,
} from '../../common/DataTypeIcons';

import { UnifiedSchema } from '../../../types/schema';
import { getTransformConfig } from '@/config/playbookTransforms';

// Define DataTypeIcon component locally for use in this file
const DataTypeIcon: React.FC<{ type: DataType; className?: string }> = ({ type, className = "h-4 w-4" }) => {
  switch (type) {
    case 'string': return <TypeIconString className={className} />;
    case 'number': return <TypeIconNumber className={className} />;
    case 'boolean': return <TypeIconBoolean className={className} />;
    case 'object': return <TypeIconObject className={className} />;
    case 'array': return <TypeIconArray className={className} />;
    case 'null': return <TypeIconNull className={className} />;
    default: return null;
  }
};

interface EnrichmentEditorProps {
  transform: PlaybookTransformItem;
  onUpdate: (updates: Partial<PlaybookTransformItem>) => void;
  availableProperties?: Parameter[];
  allEvents: EventSchema[];
  uiEventConfigs: PlaybookEventConfig[]; // New prop
  outputSchema?: UnifiedSchema | null;
  onCloseEditor: () => void;
  usedOutputProperties?: string[];
}

const EnrichmentEditor: React.FC<EnrichmentEditorProps> = ({
  transform,
  onUpdate,
  uiEventConfigs,
  outputSchema = null,
  onCloseEditor,
  usedOutputProperties = [],
}) => {
  const { portalRoot } = usePortalRoot(); // Get portalRoot from context
  const content = transform.content as PlaybookEnrichment;

  const isGlobalTransform = transform.appliesToScope === 'global';
  const isEventSpecificAndAssignedToAll =
    transform.appliesToScope === 'eventSpecific' &&
    (transform.eventIds || []).length > 0 &&
    (transform.eventIds || []).length === uiEventConfigs.length;

  const editorRef = useRef<HTMLDivElement>(null); // Ref for the editor container
  const listboxButtonRef = useRef<HTMLButtonElement>(null); // Ref for the ListboxButton
  const listboxOptionsPortalRef = useRef<HTMLDivElement>(null); // New ref for the ListboxOptionsPortal
  const [outputKeyError, setOutputKeyError] = useState<string | null>(null);
  const [valueError, setValueError] = useState<string | null>(null);

  const updateContent = (updates: Partial<PlaybookEnrichment>) => {
    onUpdate({
      content: { ...content, ...updates }
    });
  };

  // Helper to detect data type from available properties
  const detectDataType = (propertyPath: string, properties: Parameter[]): DataType | 'any' => {
    const property = properties.find(p => p.fullPath === propertyPath || p.path === propertyPath);
    if (property && property.dataTypes && property.dataTypes.length > 0) {
      return property.dataTypes[0];
    }
    return 'any'; // Default fallback to 'any'
  };

  const enrichmentTypes = [
    { value: 'DirectValue', label: 'Direct Value', fields: ['value'], outputDataType: 'any' },
    { value: 'Date', label: 'Date', fields: ['format'], outputDataType: 'string' },
    { value: 'Timestamp', label: 'Timestamp', fields: ['format'], outputDataType: 'string' },
    { value: 'UUID', label: 'UUID', fields: [], outputDataType: 'string' },
    { value: 'PseudoRandomValue', label: 'Pseudo Random Value', fields: ['length', 'charset'], outputDataType: 'string' },
  ];

  const selectedType = enrichmentTypes.find(t => t.value === content.type) || enrichmentTypes[0];

  const handleEventSelectChange = useCallback((selectedEventIds: string[]) => {
    onUpdate({ 
      eventIds: selectedEventIds,
      // Preserve existing content and type information
      content: transform.content,
      inputType: transform.inputType,
      outputType: transform.outputType
    });
  }, [onUpdate, transform.content, transform.inputType, transform.outputType]);

  const currentEventAssociations = uiEventConfigs.filter(event =>
    (transform.eventIds || []).includes(event.id)
  );

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCloseEditor();
    }
  }, [onCloseEditor]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if the click is outside the editor and also outside the ListboxOptionsPortal
      if (
        editorRef.current &&
        !editorRef.current.contains(event.target as Node) &&
        listboxOptionsPortalRef.current &&
        !listboxOptionsPortalRef.current.contains(event.target as Node)
      ) {
        onCloseEditor();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onCloseEditor]);

  // Validation for output key and value
  useEffect(() => {
    const validateFields = () => {
      // Output Key Validation
      if (content.outputKey) {
        const expectedDataType = selectedType.outputDataType;
        const actualOutputKeyDataType = detectDataType(content.outputKey, outputSchema?.parameters || []);

        if (expectedDataType !== 'any' && expectedDataType !== actualOutputKeyDataType) {
          setOutputKeyError(`Data type mismatch: Expected '${expectedDataType}', but output key is '${actualOutputKeyDataType}'.`);
        } else {
          setOutputKeyError(null);
        }
      } else {
        setOutputKeyError(null);
      }

      // Value Validation (only for DirectValue type)
      if (selectedType.value === 'DirectValue' && content.value !== undefined && content.outputKey) {
        const outputKeyDataType = detectDataType(content.outputKey, outputSchema?.parameters || []);
        let isValid = true;
        let errorMessage: string | null = null;

        switch (outputKeyDataType) {
          case 'number':
            isValid = /^-?\d+(\.\d+)?$/.test(content.value); // Matches integers and floats
            errorMessage = 'Value must be a number.';
            break;
          case 'boolean':
            isValid = ['true', 'false'].includes(content.value.toLowerCase());
            errorMessage = 'Value must be "true" or "false".';
            break;
          case 'string':
            isValid = typeof content.value === 'string';
            errorMessage = null; // Any string is valid
            break;
          case 'array':
            // For array, we might expect a JSON string representation of an array
            try {
              const parsed = JSON.parse(content.value);
              isValid = Array.isArray(parsed);
              errorMessage = 'Value must be a valid JSON array string (e.g., "[1,2,3]" or \'["a","b"]\').';
            } catch {
              isValid = false;
              errorMessage = 'Value must be a valid JSON array string (e.g., "[1,2,3]" or \'["a","b"]\').';
            }
            break;
          case 'object':
            // For object, we might expect a JSON string representation of an object
            try {
              const parsed = JSON.parse(content.value);
              isValid = typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed);
              errorMessage = 'Value must be a valid JSON object string (e.g., \'{"key":"value"}\').';
            } catch {
              isValid = false;
              errorMessage = 'Value must be a valid JSON object string (e.g., \'{"key":"value"}\').';
            }
            break;
          case 'null':
            isValid = content.value === null || content.value === 'null';
            errorMessage = 'Value must be null.';
            break;
          case 'any':
          default:
            isValid = true; // No specific validation for 'any' type
            errorMessage = null;
            break;
        }

        if (!isValid) {
          setValueError(errorMessage);
        } else {
          setValueError(null);
        }
      } else {
        setValueError(null);
      }
    };

    validateFields();
  }, [content.outputKey, content.value, content.type, outputSchema?.parameters, selectedType, detectDataType]);

  const effectiveOutputDataType = useCallback((): DataType | 'any' => {
    return selectedType.outputDataType as DataType | 'any';
  }, [selectedType]);

  const enrichmentTransformConfig = getTransformConfig('enrichment');
  const EnrichmentIcon = enrichmentTransformConfig?.icon;

  return (
    <div ref={editorRef} className="flex flex-col gap-3 py-4 px-4" onKeyDown={handleKeyDown}>
      {/* Enrichment Type and Output Key on one row */}
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Enrichment Type
          </label>
          <select
            value={content.type}
            onChange={(e) => updateContent({ type: e.target.value as any })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white text-sm"
          >
            {enrichmentTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
        {EnrichmentIcon && (
          <EnrichmentIcon size={20} className={clsx("mt-6", enrichmentTransformConfig?.colorClass.replace('bg-', 'text-'))} />
        )}
        <div className="flex-1">
          <PropertyPathAutocomplete
            label="Output Key"
            value={content.outputKey || ''}
            onSelect={(fullPath) => {
              const outputProperties = outputSchema?.parameters || [];
              const outputType = detectDataType(fullPath, outputProperties); // Detect actual data type

              onUpdate({
                content: { ...content, outputKey: fullPath }, // Update content.outputKey
                outputPath: fullPath, // Update transform.outputPath
                outputType: outputType // Use actual output data type
              });

              // Re-validate after output key changes
              const expectedDataType = selectedType.outputDataType;
              if (expectedDataType !== 'any' && expectedDataType !== outputType) {
                setOutputKeyError(`Data type mismatch: Expected '${expectedDataType}', but output key is '${outputType}'.`);
              } else {
                setOutputKeyError(null);
              }
            }}
            availableProperties={outputSchema?.parameters || []}
            usedProperties={usedOutputProperties.filter(path => path !== content.outputKey)}
            expectedDataType={effectiveOutputDataType()} // Use effective output data type
            placeholder="e.g., user.enriched_timestamp"
            className={clsx(outputKeyError && 'border-red-500 ring-red-500')}
          />
          {outputKeyError && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{outputKeyError}</p>
          )}
        </div>
      </div>

      {/* Dynamic Fields Based on Type */}
      {selectedType.fields.includes('value') && (
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Value
          </label>
          <input
            type="text"
            value={content.value || ''}
            onChange={(e) => updateContent({ value: e.target.value })}
            className={clsx(
              "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white text-sm",
              valueError && 'border-red-500 ring-red-500'
            )}
            placeholder="Enter the static value"
          />
          {valueError && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{valueError}</p>
          )}
        </div>
      )}

      {selectedType.fields.includes('format') && (
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Format
          </label>
          <input
            type="text"
            value={content.format || ''}
            onChange={(e) => updateContent({ format: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white text-sm"
            placeholder="e.g., YYYY-MM-DD or ISO8601"
          />
        </div>
      )}

      {selectedType.fields.includes('length') && (
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Length
          </label>
          <input
            type="number"
            value={content.length || 8}
            onChange={(e) => updateContent({ length: parseInt(e.target.value) || 8 })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white text-sm"
            min="1"
            max="100"
          />
        </div>
      )}

      {selectedType.fields.includes('charset') && (
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Character Set
          </label>
          <select
            value={content.charset || 'alphanumeric'}
            onChange={(e) => updateContent({ charset: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white text-sm"
          >
            <option value="alphanumeric">Alphanumeric</option>
            <option value="alphabetic">Alphabetic</option>
            <option value="numeric">Numeric</option>
            <option value="hex">Hexadecimal</option>
          </select>
        </div>
      )}

      {/* Live Preview */}
      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
        <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Preview
        </h5>
        <div className="text-sm text-gray-600 dark:text-gray-400 font-mono">
          {content.outputKey ? (
            <div className="flex items-center gap-1">
              <DataTypeIcon type={detectDataType(content.outputKey, outputSchema?.parameters || []) as DataType} />
              <span className={clsx("font-semibold", getTypeColorClass(detectDataType(content.outputKey, outputSchema?.parameters || []) as DataType))}>
                {content.outputKey}
              </span>
              <span className="text-gray-500"> = </span>
              <span className="text-green-600 dark:text-green-400">
                {content.type === 'DirectValue' ? (content.value !== undefined && content.value !== '' ? `"${content.value}"` : '""') :
                 content.type === 'UUID' ? '"550e8400-e29b-41d4-a716-446655440000"' :
                 content.type === 'Date' ? `"${content.format || 'YYYY-MM-DD'}"` : // Show format if available
                 content.type === 'Timestamp' ? `"${content.format || 'ISO8601'}"` : // Show format if available
                 content.type === 'PseudoRandomValue' ? `"${Array(content.length || 8).fill('X').join('')}"` :
                 '""'}
              </span>
            </div>
          ) : (
            <span className="text-gray-400">Enter an output key to see preview</span>
          )}
        </div>
      </div>

      {/* Assigned Events */}
      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Assigned Events
        </label>
        <div className="flex flex-row gap-2 items-end w-full">
          <Listbox
            value={currentEventAssociations}
            onChange={(selectedEvents: PlaybookEventConfig[]) => handleEventSelectChange(selectedEvents.map(event => event.id))}
            multiple
            disabled={isGlobalTransform} // Disable if global transform
          >
            {({ open }) => (
              <div className="relative flex-1">
                <ListboxButton
                  ref={listboxButtonRef} // Added ref
                  className={clsx(
                    "relative w-full cursor-default rounded border py-2 pl-3 pr-10 text-left shadow-sm focus:outline-none focus:ring-1 sm:text-sm",
                    "border-gray-300 dark:border-gray-700 dark:focus:border-blue-600 focus:ring-blue-500/30 dark:focus:ring-blue-500/20",
                    "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100",
                    isGlobalTransform && "bg-gray-100 dark:bg-gray-900 text-gray-500 dark:text-gray-400 cursor-not-allowed" // Styling for disabled state
                  )}
                >
                  <div className="flex flex-wrap gap-1 pr-8">
                    {isGlobalTransform ? (
                      <span className="text-gray-500 dark:text-gray-400">Global Transform (applies to all events)</span>
                    ) : currentEventAssociations.length > 0 ? (
                      currentEventAssociations.map(event => (
                        <span
                          key={event.id}
                          className="inline-flex items-center rounded-full bg-blue-200 px-2.5 py-0.5 text-xs font-medium text-blue-900 dark:bg-blue-700 dark:text-blue-100"
                        >
                          {event.name}
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault(); // Add preventDefault to stop default ListboxButton behavior
                              handleEventSelectChange(transform.eventIds!.filter(id => id !== event.id));
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.stopPropagation();
                                handleEventSelectChange(transform.eventIds!.filter(id => id !== event.id));
                              }
                            }}
                            className="ml-1 -mr-0.5 h-4 w-4 flex-shrink-0 rounded-full hover:bg-blue-300 dark:hover:bg-blue-600 inline-flex items-center justify-center text-blue-900 dark:text-blue-100"
                          >
                            <span className="sr-only">Remove {event.name}</span>
                            <svg className="h-2 w-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M1 1l6 6m0-6L1 7" />
                            </svg>
                          </span>
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-500 dark:text-gray-400">Select events...</span>
                    )}
                  </div>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronDown
                      className="h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </span>
                </ListboxButton>

                {portalRoot && (
                  <ListboxOptionsPortal
                    triggerRef={listboxButtonRef}
                    portalRoot={portalRoot}
                    isOpen={open && !isGlobalTransform}
                    forwardedRef={listboxOptionsPortalRef}
                  >
                    {uiEventConfigs.map((event) => (
                      <ListboxOption
                        key={event.id}
                        className={({ active }) =>
                          clsx(
                            active ? 'text-white bg-blue-600' : 'text-gray-900 dark:text-gray-100',
                            'relative cursor-pointer select-none py-2 pl-10 pr-4' // Changed cursor-default to cursor-pointer
                          )
                        }
                        value={event}
                      >
                        {({ selected, active }) => (
                          <>
                            <span className={clsx(selected ? 'font-semibold' : 'font-normal', 'block truncate')}>
                              {event.name}
                            </span>
                            {selected ? (
                              <span
                                className={clsx(
                                  active ? 'text-white' : 'text-blue-600',
                                  'absolute inset-y-0 left-0 flex items-center pl-3'
                                )}
                              >
                                <Check className="h-5 w-5" aria-hidden="true" />
                              </span>
                            ) : null}
                          </>
                        )}
                      </ListboxOption>
                    ))}
                  </ListboxOptionsPortal>
                )}
              </div>
            )}
          </Listbox>
          {isGlobalTransform ? (
            <button
              type="button"
              onClick={() => {
                const updateData = { 
                  appliesToScope: 'eventSpecific' as const, 
                  eventIds: uiEventConfigs.map(event => event.id),
                  // Preserve existing type information
                  inputType: transform.inputType,
                  outputType: transform.outputType
                };
                onUpdate(updateData);
              }}
              className={clsx(
                "flex items-center gap-2 px-4 py-2.5 rounded text-sm font-medium transition-colors",
                "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
              )}
            >
              Make Event-specific
            </button>
          ) : isEventSpecificAndAssignedToAll ? (
            <button
              type="button"
              onClick={() => {
                const updateData = { 
                  appliesToScope: 'global' as const, 
                  eventIds: undefined,
                  // Preserve existing type information
                  inputType: transform.inputType,
                  outputType: transform.outputType
                };
                onUpdate(updateData);
              }}
              className={clsx(
                "flex items-center gap-2 px-4 py-2.5 rounded text-sm font-medium transition-colors",
                "bg-blue-600 text-white hover:bg-blue-700"
              )}
            >
              Promote to Global
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                handleEventSelectChange(uiEventConfigs.map(event => event.id));
              }}
              disabled={(transform.eventIds || []).length === uiEventConfigs.length}
              className={clsx(
                "flex items-center gap-2 px-4 py-2.5 rounded text-sm font-medium transition-colors",
                "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600",
                (transform.eventIds || []).length === uiEventConfigs.length ? 'opacity-60 cursor-not-allowed' : ''
              )}
            >
              Assign All
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnrichmentEditor;

interface ListboxOptionsPortalProps {
  children: React.ReactNode;
  triggerRef: React.RefObject<HTMLElement>;
  portalRoot: HTMLElement;
  isOpen: boolean;
  forwardedRef: React.RefObject<HTMLDivElement>; 
}

const ListboxOptionsPortal: React.FC<ListboxOptionsPortalProps> = ({
  children,
  triggerRef,
  portalRoot,
  isOpen,
  forwardedRef,
}) => {
  const { tooltipStyle, tooltipRef } = useTooltipPosition(triggerRef, {
    side: 'bottom',
    align: 'start',
    sideOffset: 2,
    portalRoot,
    isOpen,
  });

  useEffect(() => {
    if (forwardedRef) {
      (forwardedRef as React.MutableRefObject<HTMLDivElement | null>).current = tooltipRef.current;
    }
  }, [forwardedRef, tooltipRef]);

  return ReactDOM.createPortal(
    <div
      ref={tooltipRef}
      style={{ ...tooltipStyle, width: tooltipStyle.width || 'auto' }}
      className="pointer-events-auto z-50"
    >
      <Transition
        show={isOpen}
        as={Fragment}
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <ListboxOptions
          static
          className="max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm
            dark:bg-gray-800 dark:border dark:border-gray-700"
        >
          {children}
        </ListboxOptions>
      </Transition>
    </div>,
    portalRoot
  );
};
